/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 18-Jul-2026
 * @author      : Vincy R
 * Requirement# : 22300
 * Purpose      : Trade Task Template Library — Provider grid + By-tenant master-detail (wide/responsive).
 */

import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import { Trade, Tenant, GlobalDefaultTemplate, Scope } from './types';
import ProviderGridView from './library/ProviderGridView';
import TenantTradeList from './library/TenantTradeList';

export interface TradeTemplateLibraryProps {
  trades: Trade[]; tenants: Tenant[]; globalDefault: GlobalDefaultTemplate;
  /** openDraft=true opens the scope's saved draft for editing; otherwise the published current. */
  onOpenScope: (scope: Scope, tenantId: string | null, tradeId: string | null, openDraft?: boolean) => void;
  /** Lazy-load a tenant's current versions when its row is selected (By-tenant view). */
  onSelectTenant?: (tenantId: string) => void;
}

/** Trade Task Template Library — Provider grid + By-tenant master-detail. */
export default function TradeTemplateLibrary({ trades, tenants, globalDefault, onOpenScope, onSelectTenant }: TradeTemplateLibraryProps) {
  const [tab, setTab] = useState<'provider' | 'tenant'>('provider');
  const [query, setQuery] = useState('');

  return (
    <Box sx={{ py: 3, px: 'clamp(24px, 4vw, 56px)', pb: 6, maxWidth: 1680, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2.25 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>Trade Task Template Library</Typography>
          <Typography variant="body2" color="text.secondary">
            {tab === 'provider'
              ? 'Provider default templates — the version a tenant adopts when they opt into a trade. Trades without a dedicated one fall back to the Global Default.'
              : 'Templates each tenant currently runs, grouped by tenant. Each tenant adopts a handful of trades.'}
          </Typography>
        </Box>
        <ToggleButtonGroup size="small" exclusive value={tab} onChange={(_, v) => v && setTab(v)}>
          <ToggleButton value="provider">Provider</ToggleButton>
          <ToggleButton value="tenant">By tenant</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <TextField fullWidth size="small" value={query} onChange={e => setQuery(e.target.value)} sx={{ mb: 2 }}
        placeholder={tab === 'provider' ? 'Search trade templates…' : "Search a tenant's trades…"}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />

      {tab === 'provider'
        ? <ProviderGridView trades={trades} globalDefault={globalDefault} query={query}
            onOpenScope={onOpenScope} />
        : <TenantTradeList trades={trades} tenants={tenants} filter={query} onOpen={onOpenScope} onSelectTenant={onSelectTenant} />}
    </Box>
  );
}
